import { call, debounce, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { FIELDS_FOR_VALIDATE, LEGAL_FORM_NAME, REQUEST_ERROR_MESSAGE } from './constants';
import {
	createLegalRequest,
	deleteLegalRequest,
	getLegalInfoRequest,
	getLegalsBySearchRequest,
	updateLegalRequest,
} from '../../api/legals-requests';
import {
	CREATE_LEGAL,
	DELETE_LEGAL,
	GET_LEGALS_OPTIONS,
	GET_LEGAL_INFO,
	UPDATE_LEGAL,
	setLegalInfoAction,
	setLegalsOptionsAction,
	setIsLegalDeletingAction,
	setIsLegalInfoLoadingAction,
	setIsLegalUpdatingAction,
	setIsLegalsOptionsLoadingAction,
	resetStateAction,
} from './actions';
import { transformToValidationErrors } from './utils';
import { getLegalFormValuesSelector, getLegalSearchValueSelector, getFormIsInvalidSelector } from './selectors';
import { DISMISS_TIME } from '../../api/constants';
import { getLegalsSelector, getRawPaginationSelector } from '../legals/selectors';
import { getLegalsAction, setPaginationAction, setLegalsAction } from '../legals/actions';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { errorHandler } from '../../api/utils';

export function* getLegalInfoSaga({ payload: id }) {
	try {
		yield put(setIsLegalInfoLoadingAction(true));

		const { data, message, toast } = yield call(getLegalInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setLegalInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLegalInfoLoadingAction(false));
	}
}

export function* createLegalSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsLegalUpdatingAction(true));

		const dataForSave = yield select(getLegalFormValuesSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(createLegalRequest, dataForSave);

		if (data) {
			const str = removeModalQuery(searchQuery);

			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${str ? `&${str}` : ''}` } : {}),
					}),
				);
			}

			const query = new URLSearchParams(searchQuery);

			yield put(getLegalsAction({ searchQuery: removeModalQuery(searchQuery.toString()), noLoading: true }));

			query.delete('modal');
			query.delete('mode');

			yield put(resetStateAction());
			redirect(`/home/legals?${query.toString()}`);
		} else {
			if (errors) {
				yield put(touch(LEGAL_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(LEGAL_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLegalUpdatingAction(false));
	}
}

export function* updateLegalSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsLegalUpdatingAction(true));

		const dataForSave = yield select(getLegalFormValuesSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(updateLegalRequest, id, dataForSave);

		if (data) {
			const str = removeModalQuery(searchQuery);

			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${str ? `&${str}` : ''}` } : {}),
					}),
				);
			}

			const legals = yield select(getLegalsSelector());

			const updatedLegal = {
				...data,
				link: `?${str ? `${str}&` : ''}modal=legal&id=${data.id}`,
			};

			yield put(
				setLegalsAction(
					legals.map(legal => {
						return legal.id === data.id ? updatedLegal : legal;
					}),
				),
			);

			yield put(setLegalInfoAction(data));

			const query = new URLSearchParams(searchQuery);
			query.delete('mode');

			redirect(`/home/legals?${query.toString()}`);
			yield put(getLegalsAction({ searchQuery: removeModalQuery(searchQuery.toString()), noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(LEGAL_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(LEGAL_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLegalUpdatingAction(false));
	}
}

export function* deleteLegalSaga({ payload: { id, redirect, query } }) {
	try {
		yield put(setIsLegalDeletingAction(true));

		const { data, message, toast } = yield call(deleteLegalRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const legals = yield select(getLegalsSelector());

			yield put(setLegalsAction(legals.filter(({ id }) => id !== data.id)));

			const pagination = yield select(getRawPaginationSelector());

			if (pagination) {
				yield put(setPaginationAction({ ...pagination, total: pagination.total - 1 }));
			}

			redirect();
			yield put(getLegalsAction({ searchQuery: removeModalQuery(query.toString()), noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLegalDeletingAction(false));
	}
}

export function* getLegalsSaga() {
	try {
		yield put(setIsLegalsOptionsLoadingAction(true));

		const searchQuery = yield select(getLegalSearchValueSelector());

		const response = yield call(getLegalsBySearchRequest, searchQuery);

		if (response?.suggestions) {
			yield put(setLegalsOptionsAction(response.suggestions));
		} else {
			throw new Error(response?.message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLegalsOptionsLoadingAction(false));
	}
}

export default function* legalModalPageSaga() {
	yield takeEvery(GET_LEGAL_INFO, getLegalInfoSaga);
	yield takeEvery(CREATE_LEGAL, createLegalSaga);
	yield takeEvery(UPDATE_LEGAL, updateLegalSaga);
	yield takeEvery(DELETE_LEGAL, deleteLegalSaga);
	yield debounce(300, GET_LEGALS_OPTIONS, getLegalsSaga);
}
